import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useRestaurant } from '../../context/RestaurantContext';
import { useCurrency } from '../../context/CurrencyContext';
import { 
  Plus, Edit, Trash2, Search, Upload, Scan, X, Check
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { ImageUpload } from '../../components/ImageUpload';
import { toast } from 'sonner';

export default function MenuManagement() {
  const { t } = useTranslation();
  const { api } = useAuth();
  // Use shared restaurant context
  const { restaurants, selectedRestaurant, setSelectedRestaurant } = useRestaurant();
  const { formatPrice, currencyConfig } = useCurrency();
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [scanning, setScanning] = useState(false);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    category: '',
    image_url: '',
    is_available: true,
    allergens: '',
    dietary_info: '',
  });

  useEffect(() => {
    if (selectedRestaurant) {
      fetchMenu();
    }
  }, [selectedRestaurant]);

  const fetchMenu = async () => {
    if (!selectedRestaurant) return;
    try {
      setLoading(true);
      const response = await api.get(`/restaurants/${selectedRestaurant.id}/menu`);
      setMenuItems(response.data);
    } catch (error) {
      console.error('Failed to fetch menu:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const data = {
        ...formData,
        price: parseFloat(formData.price),
        allergens: formData.allergens.split(',').map(s => s.trim()).filter(Boolean),
        dietary_info: formData.dietary_info.split(',').map(s => s.trim()).filter(Boolean),
      };

      if (editItem) {
        await api.put(`/menu/${editItem.id}`, data);
        toast.success('Menu item updated');
      } else {
        await api.post(`/restaurants/${selectedRestaurant.id}/menu`, data);
        toast.success('Menu item added');
      }

      fetchMenu();
      resetForm();
    } catch (error) {
      toast.error('Failed to save menu item');
    }
  };

  const handleDelete = async (itemId) => {
    if (!confirm('Are you sure you want to delete this item?')) return;
    
    try {
      await api.delete(`/menu/${itemId}`);
      toast.success('Menu item deleted');
      fetchMenu();
    } catch (error) {
      toast.error('Failed to delete menu item');
    }
  };

  const handleAIScan = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setScanning(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post(
        `/restaurants/${selectedRestaurant.id}/menu/ai-scan`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success(response.data.message);
      fetchMenu();
    } catch (error) {
      toast.error('Failed to scan menu');
    } finally {
      setScanning(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      price: '',
      category: '',
      image_url: '',
      is_available: true,
      allergens: '',
      dietary_info: '',
    });
    setEditItem(null);
    setIsAddOpen(false);
  };

  const openEdit = (item) => {
    setFormData({
      name: item.name,
      description: item.description || '',
      price: item.price.toString(),
      category: item.category,
      image_url: item.image_url || '',
      is_available: item.is_available,
      allergens: item.allergens?.join(', ') || '',
      dietary_info: item.dietary_info?.join(', ') || '',
    });
    setEditItem(item);
    setIsAddOpen(true);
  };

  const filteredItems = menuItems.filter(item =>
    item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const categories = [...new Set(menuItems.map(item => item.category))];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl font-bold text-slate-900">{t('menuMgmt.title')}</h1>
        <div className="flex gap-2">
          {/* AI Scan Button */}
          <input
            type="file"
            ref={fileInputRef}
            className="hidden"
            accept="image/*"
            onChange={handleAIScan}
          />
          <Button
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={scanning || !selectedRestaurant}
            data-testid="ai-scan-button"
          >
            {scanning ? (
              <>{t('menuMgmt.scanning') || 'Processing...'}</>
            ) : (
              <>
                <Scan className="w-4 h-4 mr-2" />
                {t('menuMgmt.scanMenu')}
              </>
            )}
          </Button>

          {/* Add Item Button */}
          <Dialog open={isAddOpen} onOpenChange={(open) => { setIsAddOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="add-menu-item-button">
                <Plus className="w-4 h-4 mr-2" />
                {t('menuMgmt.addItem')}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editItem ? t('menuMgmt.editItem') : t('menuMgmt.addItem')}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="name">{t('menuMgmt.itemName')}</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    data-testid="menu-item-name"
                  />
                </div>
                <div>
                  <Label htmlFor="description">{t('menuMgmt.description')}</Label>
                  <Textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={2}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="price">{t('menuMgmt.price')}</Label>
                    <Input
                      id="price"
                      name="price"
                      type="number"
                      step="0.01"
                      value={formData.price}
                      onChange={handleChange}
                      required
                      data-testid="menu-item-price"
                    />
                  </div>
                  <div>
                    <Label htmlFor="category">{t('menuMgmt.category')}</Label>
                    <Input
                      id="category"
                      name="category"
                      value={formData.category}
                      onChange={handleChange}
                      placeholder="e.g., Starters, Mains"
                      required
                      data-testid="menu-item-category"
                    />
                  </div>
                </div>
                <div>
                  <Label>{t('menuMgmt.image')}</Label>
                  <div className="mt-2">
                    <ImageUpload
                      endpoint={editItem ? `/upload/image/menu/${editItem.id}` : '/upload/image'}
                      category="menu"
                      currentImage={formData.image_url}
                      onUpload={(url) => setFormData({ ...formData, image_url: url })}
                      placeholder="Upload item image"
                      aspectRatio="aspect-square"
                      className="max-w-[200px]"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="allergens">{t('menuMgmt.allergens')}</Label>
                  <Input
                    id="allergens"
                    name="allergens"
                    value={formData.allergens}
                    onChange={handleChange}
                    placeholder="Gluten, Dairy, Nuts"
                  />
                </div>
                <div>
                  <Label htmlFor="dietary_info">{t('menuMgmt.dietaryInfo')}</Label>
                  <Input
                    id="dietary_info"
                    name="dietary_info"
                    value={formData.dietary_info}
                    onChange={handleChange}
                    placeholder="Vegetarian, Vegan, Halal"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    id="is_available"
                    checked={formData.is_available}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_available: checked })}
                  />
                  <Label htmlFor="is_available">{t('menuMgmt.available')}</Label>
                </div>
                <div className="flex gap-2 justify-end">
                  <Button type="button" variant="outline" onClick={resetForm}>
                    {t('common.cancel') || 'Cancel'}
                  </Button>
                  <Button type="submit" className="btn-primary" data-testid="save-menu-item">
                    {editItem ? t('common.update') || 'Update' : t('common.add') || 'Add'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
        <Input
          placeholder={t('menuMgmt.searchMenu')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
          data-testid="search-menu"
        />
      </div>

      {/* Menu Items by Category */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 bg-white rounded-xl animate-pulse"></div>
          ))}
        </div>
      ) : categories.length > 0 ? (
        <div className="space-y-8">
          {categories.map((category) => (
            <div key={category}>
              <h2 className="text-lg font-semibold text-slate-900 mb-4">{category}</h2>
              <div className="grid md:grid-cols-2 gap-4">
                {filteredItems
                  .filter(item => item.category === category)
                  .map((item) => (
                    <div
                      key={item.id}
                      className="bg-white rounded-xl p-4 shadow-sm flex gap-4"
                      data-testid={`menu-item-${item.id}`}
                    >
                      {item.image_url && (
                        <img
                          src={item.image_url}
                          alt={item.name}
                          className="w-20 h-20 rounded-lg object-cover"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="font-medium text-slate-900">{item.name}</h3>
                            <p className="text-sm text-slate-500 line-clamp-1">{item.description}</p>
                          </div>
                          <span className="font-bold text-orange-500">{formatPrice(item.price)}</span>
                        </div>
                        <div className="flex justify-between items-center mt-2">
                          <span className={`text-xs px-2 py-1 rounded ${item.is_available ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {item.is_available ? 'Available' : 'Unavailable'}
                          </span>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEdit(item)}
                              data-testid={`edit-${item.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-red-500"
                              onClick={() => handleDelete(item.id)}
                              data-testid={`delete-${item.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-16 bg-white rounded-xl">
          <UtensilsCrossed className="w-12 h-12 mx-auto text-slate-300 mb-4" />
          <h2 className="text-xl font-semibold text-slate-900 mb-2">No menu items</h2>
          <p className="text-slate-500 mb-6">Start by adding items manually or scan an existing menu</p>
        </div>
      )}
    </div>
  );
}

function UtensilsCrossed(props) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="m16 2-2.3 2.3a3 3 0 0 0 0 4.2l1.8 1.8a3 3 0 0 0 4.2 0L22 8"/>
      <path d="M15 15 3.3 3.3a4.2 4.2 0 0 0 0 6l7.3 7.3c.7.7 2 .7 2.8 0L15 15Zm0 0 7 7"/>
      <path d="m2.1 21.8 6.4-6.3"/>
      <path d="m19 5-7 7"/>
    </svg>
  );
}
